/**
 * THE WORDS OF THE WORKSPACE'S OWN SETTINGS.
 *
 * ── THE TWO LANGUAGES ON ONE SCREEN, AND WHY THEY ARE NOT THE SAME FIELD ──
 *
 * `prefs.language` is the WORKSPACE's language: what the business speaks to its
 * customers, and what the public chat, a document, an agent's answer and an
 * invoice line come out in. The panel language — what THIS colleague reads — is
 * a different question with a different owner (`PanelLanguageCard`), and the
 * whole reason both exist is that a Brazilian operator may work with the panel
 * in English without their customers ever hearing a word of it.
 *
 * So this card deliberately does NOT restrict its language field to the two the
 * panel speaks: a shop in Lisbon formatting money and dates as `pt-PT` is a
 * legitimate answer, and narrowing it to `en`/`pt-BR` would take a capability
 * away to make a form look tidy.
 */
export const workspaceMessages = {
	en: {
		prefs: {
			title: "Preferences",
			subtitle: "The workspace name, and how it reads dates, times and money",
			name: "Workspace name",
			timezone: "Time zone",
			timezoneHint: "Decides what “today” means in reports and when schedules run.",
			language: "Language",
			languageHint: "What this business speaks to its customers.",
			currency: "Currency",
			country: "Country",
			countryHint: "Sets which validator a national document goes through.",
			previewTitle: "How it will look",
			previewWhen: "Date and time",
			previewMoney: "Amount",
			adminOnly: "Only an administrator changes the workspace preferences.",
			saved: "Preferences saved.",
			failed: "could not save",
		},
		/**
		 * The rest of the workspace's own settings, added to this dictionary rather
		 * than to a second one: `PreferencesCard` already mounts this scope, and a
		 * `workspace-cards.ts` beside it would mean two files answering "what does
		 * this workspace's settings screen say".
		 */
		agentDisplay: {
			title: "What shows while an agent works",
			lead: "It applies to everyone in this workspace. It does not change what the agent DOES — only what the team sees while it does it.",
			reasoning: "Show the reasoning",
			reasoningHint:
				"What it thought before answering, in a block that opens. Useful for tuning the persona; noise for anyone who just wants the answer.",
			tools: "Show the tools",
			toolsHint:
				"Which tools it used, with input and output. It is how you find out why an answer came out wrong.",
			cost: "Show the consumption",
			costHint: "The credits consumed by each answer.",
			adminOnly: "Only an administrator changes this.",
			failed: "Could not save that change.",
		},
		brand: {
			loadFailed: "Could not load the visual identity.",
			identityHint:
				"Visual identity used by Ben for sites, screens and materials. Behavior belongs in workspace rules or each agent’s limits.",
			slogan: "Slogan",
			typography: "Typography",
			undecided: "Decide later",
			sans: "Sans serif",
			serif: "Serif",
			mono: "Monospace",
			colors: "Colors",
			colorsHint: "Ordered palette: main color first.",
			color: "Color {n}",
			colorCode: "Hex code {n}",
			removeColor: "Remove color",
			addColor: "Add color",

			title: "Brand",
			subtitle: "The logo that appears in the panel, on the published site and on the forms",
			uploadFailed: "Could not upload the image right now.",
			sendFailed: "could not upload the image",
			removeFailed: "could not remove it",
			hint: "PNG, JPG, SVG or WebP, up to 2 MB. A transparent background looks better: the logo appears over light and over dark. Whoever opens the site or the published form sees this image.",
			adminOnly: "Only an administrator changes the workspace's brand.",
			updated: "Brand updated.",
			removed: "Brand removed.",
			tooBig: "The image is over 2 MB. A logo fits well under that.",
			/** O mesmo botão nos dois estados: já há logo, ou ainda não há. */
			replace: "Replace",
			upload: "Upload logo",
		},
		business: {
			title: "The business",
			subtitle: "What every agent in this workspace already knows, before each one's persona",
			saved: "The business was saved.",
			failed: "could not save",
			placeholder: "Segment, what is sold, to whom, what is important to know.",
			boundaryHint:
				"A constraint, not context — it reaches the agent as a block of its own, before the persona.",
			logoAlt: "Business logo",
			logoCaption: "Captured during onboarding. Replace it under Brand.",
			/** As duas caixas do cartão: o que a empresa faz, e o que ninguém pode dizer. */
			whatLabel: "What the company does",
			whatHint: "Context. It holds for every agent, without repeating it in each persona.",
			rulesLabel: "Fixed rules",
			rulesPlaceholder: "What no agent may say, promise or guarantee.",
		},
		company: {
			subtitle: "What a contract, a quote or a receipt prints about whoever issues it",
			legalName: "Legal name",
			legalNameHint:
				"The name a document is issued under, when it differs from the workspace's name.",
			taxIdHint: "Stored as you write it. The field's name comes from the workspace's country.",
			/**
			 * O rótulo do documento fiscal, com e sem o nome local.
			 *
			 * Estava montado à mão no componente — `` `Documento fiscal (${local})` `` —
			 * e por isso um painel em inglês desenhava "Documento fiscal (EIN)" sobre
			 * um campo de uma empresa americana: metade traduzida e metade não, na
			 * mesma linha. A PALAVRA LOCAL (EIN, CNPJ, NIF) não se traduz — é o nome
			 * próprio do documento naquele país — e por isso entra como parâmetro.
			 */
			taxId: "Tax ID",
			taxIdNamed: "Tax ID ({local})",
			emailInvalid: "The @ is missing — this is where a customer replies to the document.",
			phoneHint:
				"How it should print. It is not the number that receives notices — that one is yours, under “You”.",
			adminOnly: "Only an administrator changes the company's registration.",
			failed: "could not save",
			line1: "Address",
			line1Placeholder: "Street, avenue, number",
			region: "Region / state",
			postalCode: "Postcode",
			country: "Country",
			title: "The company",
			line2: "Second line",
			line2Placeholder: "Suite, floor, block",
			city: "City",
			contactEmail: "Contact e-mail",
			phone: "Phone",
		},
		presence: {
			mine: "My presence",
			state: "My state",
			available: "available",
			busy: "busy",
			away: "away",
			atMost: "at most",
			noCeiling: "no ceiling",
			ceilingLabel: "Ceiling of simultaneous conversations",
			conversations: "{n} conversation | {n} conversations",
			conversationsOf: "{n} conversation of {max} | {n} conversations of {max}",
		},
		signature: {
			lead: "What the customer reads before the words, when the one answering is a person. The attendant's messages and the automations' are not signed — whoever wants the attendant to introduce itself says so in its persona.",
			none: "Do not sign",
			noneHint: "Just the words, as it has always been.",
			name: "Name of whoever answers",
			nameHint: "Whoever is at the keyboard right now.",
			titleOnly: "The title only",
			titleOnlyHint: "The team speaks as one voice.",
			nameTitle: "Name and title",
			nameTitleHint: "Both, separated by a dash.",
			title: "Signature on the answers",
			titleLabel: "Title",
			titlePlaceholder: "Attendant",
			howItReads: "How the customer will read it",
			sample: "Hi! I have already set your order aside, it arrives on Thursday.",
			noSignature: "No signature: the customer does not get to know who answered.",
			adminOnly: "Only an administrator changes this.",
			failed: "could not save",
		},
		teams: {
			title: "Teams",
			subtitle: "Groups a conversation can be transferred to",
			notAccess:
				"Teams organise the service queue and the notifications. They do not change what each person sees — for that, use Access profiles in Settings → Access.",
			notAccessLink: "Open Access",
			empty: "No team yet. A conversation transferred with no team stays in the general queue.",
			members: "{n} person | {n} people",
			whoIsIn: "who is in",
			failed: "no luck",
			itIsYou: "it is you",
			soleOwner: "the only active owner",
			neverSignedIn: "never signed in",
			/**
			 * Why a change to a colleague was refused, keyed by the server's CODE.
			 *
			 * Never the server's own sentence: the API answers in English, and
			 * `LAST_OWNER` is not an accident somebody trips once — it is what a
			 * workspace with a single owner gets every single time it tries.
			 */
			error: {
				lastOwner: "This workspace needs at least one active owner. Promote somebody else first.",
				forbidden: "Only an administrator can do that.",
				notFound: "That person is no longer in this workspace.",
				network: "Could not reach the server.",
				failed: "Could not save that change.",
			},
			now: "now",
			minutesAgo: "{n} min ago",
			hoursAgo: "{n}h ago",
			daysAgo: "{n} day ago | {n} days ago",
		},
		account: {
			title: "You",
			subtitle: "Your name, your role and your password",
			/** O mesmo botão nos dois estados: já há foto, ou ainda não há. */
			photoReplace: "Replace photo",
			photoUpload: "Upload photo",
			photoFailed: "could not upload the photo",
			photoRemoveFailed: "could not remove the photo",
			failed: "could not save",
			wrongPassword: "the current password does not match",
			passwordFailed: "could not change the password",
			phoneInvalid:
				"Start with + and the country code — without it the number is ambiguous between countries.",
			phoneHint:
				"This is where a notice reaches you outside the panel. Leave it blank to receive nothing.",
			emailNote: "Sign-in e-mail: {email}",
			passwordNote:
				"On changing it, every session of yours drops — including this one. You come back in with the new password.",
			yourName: "Your name",
			yourWhatsapp: "Your WhatsApp",
			saved: "Saved.",
			changePassword: "Change the password",
			currentPassword: "Current password",
			newPassword: "New password",
			changeEmail: "Change the sign-in e-mail",
			newEmail: "New e-mail",
			sendLink: "Send confirmation link",
			emailSent:
				"We sent a link to {email}. Open it to confirm — until then you keep signing in with the current e-mail.",
			emailTaken: "This e-mail is already in use by another account.",
			emailInvalid: "This e-mail does not look valid.",
			emailSame: "This is already your e-mail.",
			emailFailed: "could not request the change",
		},
		error: {
			forbidden: "Only an administrator can do that.",
			notFound: "That person is no longer in this workspace.",
			offline: "Could not reach the server.",
			generic: "Could not save that change.",
		},
	},
	"pt-BR": {
		prefs: {
			title: "Preferências",
			subtitle: "O nome do workspace e como ele lê datas, horas e dinheiro",
			name: "Nome do workspace",
			timezone: "Fuso horário",
			timezoneHint: "Decide o que é “hoje” nos relatórios e a que hora os agendamentos rodam.",
			language: "Idioma",
			languageHint: "O que este negócio fala com os clientes dele.",
			currency: "Moeda",
			country: "País",
			countryHint: "Define por qual validador passa um documento nacional.",
			previewTitle: "Como vai aparecer",
			previewWhen: "Data e hora",
			previewMoney: "Valor",
			adminOnly: "Só um administrador muda as preferências do workspace.",
			saved: "Preferências salvas.",
			failed: "não deu para salvar",
		},
		agentDisplay: {
			title: "O que aparece quando um agente trabalha",
			lead: "Vale para todo mundo neste workspace. Não muda o que o agente FAZ — só o que a equipe vê enquanto ele faz.",
			reasoning: "Mostrar o raciocínio",
			reasoningHint:
				"O que ele pensou antes de responder, num bloco que abre. Útil para ajustar a persona; ruído para quem só quer a resposta.",
			tools: "Mostrar as ferramentas",
			toolsHint:
				"Quais ferramentas ele usou, com entrada e saída. É como se descobre por que a resposta veio errada.",
			cost: "Mostrar o consumo",
			costHint: "Os créditos consumidos por cada resposta.",
			adminOnly: "Só um administrador muda isto.",
			failed: "Não deu para salvar essa mudança.",
		},
		brand: {
			loadFailed: "Não foi possível carregar a identidade visual.",
			identityHint:
				"Identidade visual usada pelo Ben em sites, telas e materiais. Comportamento fica nas regras do workspace ou nos limites de cada agente.",
			slogan: "Slogan",
			typography: "Tipografia",
			undecided: "Definir depois",
			sans: "Sem serifa",
			serif: "Com serifa",
			mono: "Monoespaçada",
			colors: "Cores",
			colorsHint: "Paleta em ordem: cor principal primeiro.",
			color: "Cor {n}",
			colorCode: "Código hexadecimal {n}",
			removeColor: "Remover cor",
			addColor: "Adicionar cor",

			title: "Marca",
			subtitle: "O logo que aparece no painel, no site publicado e nos formulários",
			uploadFailed: "Não foi possível enviar a imagem agora.",
			sendFailed: "não deu para enviar a imagem",
			removeFailed: "não deu para remover",
			hint: "PNG, JPG, SVG ou WebP, até 2 MB. Fundo transparente fica melhor: o logo aparece sobre o claro e sobre o escuro. Quem abre o site ou o formulário publicado vê esta imagem.",
			adminOnly: "Só um administrador troca a marca do workspace.",
			updated: "Marca atualizada.",
			removed: "Marca removida.",
			tooBig: "A imagem passa de 2 MB. Um logo cabe bem abaixo disso.",
			replace: "Trocar",
			upload: "Enviar logo",
		},
		business: {
			title: "O negócio",
			subtitle: "O que todo agente deste workspace já sabe, antes da persona de cada um",
			saved: "O negócio foi salvo.",
			failed: "não deu para salvar",
			placeholder: "Segmento, o que é vendido, para quem, o que é importante saber.",
			boundaryHint:
				"Restrição, não contexto — chega ao agente como um bloco próprio, antes da persona.",
			logoAlt: "Logo do negócio",
			logoCaption: "Capturado no onboarding. Troque em Marca.",
			whatLabel: "O que a empresa faz",
			whatHint: "Contexto. Vale para todos os agentes, sem repetir na persona de cada um.",
			rulesLabel: "Regras fixas",
			rulesPlaceholder: "O que nenhum agente pode dizer, prometer ou garantir.",
		},
		company: {
			subtitle: "O que um contrato, um orçamento ou um recibo imprime sobre quem emite",
			legalName: "Razão social",
			legalNameHint:
				"O nome sob o qual o documento é emitido, quando é diferente do nome do workspace.",
			taxIdHint: "Guardado como você escrever. O nome do campo vem do país do workspace.",
			taxId: "Documento fiscal",
			taxIdNamed: "Documento fiscal ({local})",
			emailInvalid: "Falta o @ — é para onde um cliente responde o documento.",
			phoneHint:
				"Como deve sair impresso. Não é o número que recebe avisos — esse é o seu, em “Você”.",
			adminOnly: "Só um administrador muda o cadastro da empresa.",
			failed: "não deu para salvar",
			line1: "Endereço",
			line1Placeholder: "Rua, avenida, número",
			region: "Região / estado",
			postalCode: "Código postal",
			country: "País",
			title: "A empresa",
			line2: "Complemento",
			line2Placeholder: "Sala, andar, bloco",
			city: "Cidade",
			contactEmail: "E-mail de contato",
			phone: "Telefone",
		},
		presence: {
			mine: "Minha presença",
			state: "Meu estado",
			available: "disponível",
			busy: "ocupado",
			away: "ausente",
			atMost: "no máximo",
			noCeiling: "sem teto",
			ceilingLabel: "Teto de conversas ao mesmo tempo",
			conversations: "{n} conversa | {n} conversas",
			conversationsOf: "{n} conversa de {max} | {n} conversas de {max}",
		},
		signature: {
			lead: "O que o cliente lê antes das palavras, quando quem responde é uma pessoa. Mensagens do atendente e das automações não são assinadas — quem quiser o atendente se apresentando diz isso na persona dele.",
			none: "Não assinar",
			noneHint: "Só as palavras, como sempre foi.",
			name: "Nome de quem responde",
			nameHint: "Quem está no teclado agora.",
			titleOnly: "Só o título",
			titleOnlyHint: "A equipe fala como uma voz só.",
			nameTitle: "Nome e título",
			nameTitleHint: "Os dois, separados por travessão.",
			title: "Assinatura nas respostas",
			titleLabel: "Título",
			titlePlaceholder: "Atendente",
			howItReads: "Como o cliente vai ler",
			sample: "Oi! Já separei o seu pedido, chega quinta.",
			noSignature: "Sem assinatura: o cliente não fica sabendo quem respondeu.",
			adminOnly: "Só um administrador muda isto.",
			failed: "não deu para salvar",
		},
		teams: {
			title: "Times",
			subtitle: "Grupos para quem uma conversa pode ser transferida",
			notAccess:
				"Times organizam a fila de atendimento e os avisos. Não mudam o que cada pessoa vê — para isso use Perfis de acesso em Configurações → Acessos.",
			notAccessLink: "Abrir Acessos",
			empty: "Nenhum time ainda. Uma conversa transferida sem time fica na fila geral.",
			members: "{n} pessoa | {n} pessoas",
			whoIsIn: "quem está",
			failed: "não deu",
			itIsYou: "é você",
			soleOwner: "único dono ativo",
			neverSignedIn: "nunca entrou",
			error: {
				lastOwner:
					"Este workspace precisa de pelo menos um dono ativo. Promova outra pessoa antes.",
				forbidden: "Só um administrador pode fazer isso.",
				notFound: "Essa pessoa não está mais neste workspace.",
				network: "Não consegui falar com o servidor.",
				failed: "Não deu para salvar essa mudança.",
			},
			now: "agora",
			minutesAgo: "há {n} min",
			hoursAgo: "há {n}h",
			daysAgo: "há {n} dia | há {n} dias",
		},
		account: {
			title: "Você",
			subtitle: "Seu nome, seu papel e sua senha",
			photoReplace: "Trocar foto",
			photoUpload: "Enviar foto",
			photoFailed: "não deu para enviar a foto",
			photoRemoveFailed: "não deu para remover a foto",
			failed: "não deu para salvar",
			wrongPassword: "a senha atual não confere",
			passwordFailed: "não deu para trocar a senha",
			phoneInvalid: "Comece com + e o código do país — sem ele o número é ambíguo entre países.",
			phoneHint:
				"É por aqui que um aviso te alcança fora do painel. Deixe em branco para não receber nada.",
			emailNote: "E-mail de acesso: {email}",
			passwordNote:
				"Ao trocar, todas as suas sessões caem — inclusive esta. Você entra de novo com a senha nova.",
			yourName: "Seu nome",
			yourWhatsapp: "Seu WhatsApp",
			saved: "Salvo.",
			changePassword: "Trocar a senha",
			currentPassword: "Senha atual",
			newPassword: "Senha nova",
			changeEmail: "Trocar o e-mail de acesso",
			newEmail: "E-mail novo",
			sendLink: "Enviar link de confirmação",
			emailSent:
				"Enviamos um link para {email}. Abra para confirmar — até lá você continua entrando com o e-mail atual.",
			emailTaken: "Este e-mail já é usado por outra conta.",
			emailInvalid: "Este e-mail não parece válido.",
			emailSame: "Este já é o seu e-mail.",
			emailFailed: "não deu para pedir a troca",
		},
		error: {
			forbidden: "Só um administrador pode fazer isso.",
			notFound: "Essa pessoa não está mais neste workspace.",
			offline: "Não consegui falar com o servidor.",
			generic: "Não deu para salvar essa mudança.",
		},
	},
	el: {
		prefs: {
			title: "Προτιμήσεις",
			subtitle: "Το όνομα του χώρου εργασίας και πώς διαβάζει ημερομηνίες, ώρες και χρήματα",
			name: "Όνομα χώρου εργασίας",
			timezone: "Ζώνη ώρας",
			timezoneHint:
				"Καθορίζει τι σημαίνει «σήμερα» στις αναφορές και πότε τρέχουν τα χρονοδιαγράμματα.",
			language: "Γλώσσα",
			languageHint: "Αυτό που μιλά αυτή η επιχείρηση στους πελάτες της.",
			currency: "Νόμισμα",
			country: "Χώρα",
			countryHint: "Ορίζει από ποιον έλεγχο εγκυρότητας περνά ένα εθνικό έγγραφο.",
			previewTitle: "Πώς θα φαίνεται",
			previewWhen: "Ημερομηνία και ώρα",
			previewMoney: "Ποσό",
			adminOnly: "Μόνο ένας διαχειριστής αλλάζει τις προτιμήσεις του χώρου εργασίας.",
			saved: "Οι προτιμήσεις αποθηκεύτηκαν.",
			failed: "δεν ήταν δυνατή η αποθήκευση",
		},
		agentDisplay: {
			title: "Τι εμφανίζεται όσο δουλεύει ένας πράκτορας",
			lead: "Ισχύει για όλους σε αυτόν τον χώρο εργασίας. Δεν αλλάζει τι ΚΑΝΕΙ ο πράκτορας — μόνο τι βλέπει η ομάδα όσο το κάνει.",
			reasoning: "Εμφάνιση του συλλογισμού",
			reasoningHint:
				"Τι σκέφτηκε πριν απαντήσει, σε ένα μπλοκ που ανοίγει. Χρήσιμο για να ρυθμίσεις την περσόνα· θόρυβος για όποιον θέλει μόνο την απάντηση.",
			tools: "Εμφάνιση των εργαλείων",
			toolsHint:
				"Ποια εργαλεία χρησιμοποίησε, με είσοδο και έξοδο. Έτσι ανακαλύπτεις γιατί μια απάντηση βγήκε λάθος.",
			cost: "Εμφάνιση της κατανάλωσης",
			costHint: "Οι μονάδες που καταναλώνει κάθε απάντηση.",
			adminOnly: "Μόνο ένας διαχειριστής το αλλάζει αυτό.",
			failed: "Δεν ήταν δυνατή η αποθήκευση αυτής της αλλαγής.",
		},
		brand: {
			loadFailed: "Δεν ήταν δυνατή η φόρτωση της οπτικής ταυτότητας.",
			identityHint:
				"Οπτική ταυτότητα που χρησιμοποιεί ο Ben για ιστοτόπους, οθόνες και υλικό. Οι κανόνες συμπεριφοράς ορίζονται χωριστά.",
			slogan: "Σλόγκαν",
			typography: "Τυπογραφία",
			undecided: "Αργότερα",
			sans: "Χωρίς πατούρες",
			serif: "Με πατούρες",
			mono: "Σταθερό πλάτος",
			colors: "Χρώματα",
			colorsHint: "Πρώτα το κύριο χρώμα.",
			color: "Χρώμα {n}",
			colorCode: "Κωδικός hex {n}",
			removeColor: "Αφαίρεση χρώματος",
			addColor: "Προσθήκη χρώματος",

			title: "Μάρκα",
			subtitle: "Το λογότυπο που εμφανίζεται στο πάνελ, στον δημοσιευμένο ιστότοπο και στις φόρμες",
			uploadFailed: "Δεν ήταν δυνατή η μεταφόρτωση της εικόνας αυτή τη στιγμή.",
			sendFailed: "δεν ήταν δυνατή η μεταφόρτωση της εικόνας",
			removeFailed: "δεν ήταν δυνατή η αφαίρεσή του",
			hint: "PNG, JPG, SVG ή WebP, έως 2 MB. Το διαφανές φόντο δείχνει καλύτερα: το λογότυπο εμφανίζεται πάνω σε ανοιχτό και πάνω σε σκούρο. Όποιος ανοίγει τον ιστότοπο ή τη δημοσιευμένη φόρμα βλέπει αυτή την εικόνα.",
			adminOnly: "Μόνο ένας διαχειριστής αλλάζει τη μάρκα του χώρου εργασίας.",
			updated: "Η μάρκα ενημερώθηκε.",
			removed: "Η μάρκα αφαιρέθηκε.",
			tooBig: "Η εικόνα ξεπερνά τα 2 MB. Ένα λογότυπο χωράει άνετα κάτω από αυτό.",
			replace: "Αντικατάσταση",
			upload: "Μεταφόρτωση λογότυπου",
		},
		business: {
			title: "Η επιχείρηση",
			subtitle:
				"Αυτό που κάθε πράκτορας σε αυτόν τον χώρο εργασίας ξέρει ήδη, πριν από την περσόνα του καθενός",
			saved: "Η επιχείρηση αποθηκεύτηκε.",
			failed: "δεν ήταν δυνατή η αποθήκευση",
			placeholder: "Κλάδος, τι πουλιέται, σε ποιον, τι είναι σημαντικό να ξέρει κανείς.",
			boundaryHint:
				"Περιορισμός, όχι πλαίσιο — φτάνει στον πράκτορα ως ξεχωριστό μπλοκ, πριν από την περσόνα.",
			logoAlt: "Λογότυπο της επιχείρησης",
			logoCaption: "Καταγράφηκε κατά την αρχική ρύθμιση. Αντικατέστησέ το στη Μάρκα.",
			whatLabel: "Τι κάνει η επιχείρηση",
			whatHint: "Πλαίσιο. Ισχύει για κάθε πράκτορα, χωρίς να επαναλαμβάνεται σε κάθε περσόνα.",
			rulesLabel: "Σταθεροί κανόνες",
			rulesPlaceholder: "Τι δεν επιτρέπεται να πει, να υποσχεθεί ή να εγγυηθεί κανένας πράκτορας.",
		},
		company: {
			subtitle: "Τι τυπώνει ένα συμβόλαιο, μια προσφορά ή μια απόδειξη για όποιον το εκδίδει",
			legalName: "Επωνυμία",
			legalNameHint:
				"Το όνομα με το οποίο εκδίδεται ένα έγγραφο, όταν διαφέρει από το όνομα του χώρου εργασίας.",
			taxIdHint:
				"Αποθηκεύεται όπως το γράφεις. Το όνομα του πεδίου προκύπτει από τη χώρα του χώρου εργασίας.",
			taxId: "Φορολογικός αριθμός",
			taxIdNamed: "Φορολογικός αριθμός ({local})",
			emailInvalid: "Λείπει το παπάκι — εδώ απαντά ένας πελάτης στο έγγραφο.",
			phoneHint:
				"Πώς πρέπει να τυπώνεται. Δεν είναι ο αριθμός που λαμβάνει ειδοποιήσεις — εκείνος είναι ο δικός σου, στο «Εσύ».",
			adminOnly: "Μόνο ένας διαχειριστής αλλάζει τα στοιχεία μητρώου της επιχείρησης.",
			failed: "δεν ήταν δυνατή η αποθήκευση",
			line1: "Διεύθυνση",
			line1Placeholder: "Οδός, λεωφόρος, αριθμός",
			region: "Περιοχή / νομός",
			postalCode: "Ταχυδρομικός κώδικας",
			country: "Χώρα",
			title: "Η επιχείρηση",
			line2: "Δεύτερη γραμμή",
			line2Placeholder: "Γραφείο, όροφος, κτίριο",
			city: "Πόλη",
			contactEmail: "Email επικοινωνίας",
			phone: "Τηλέφωνο",
		},
		presence: {
			mine: "Η παρουσία μου",
			state: "Η κατάστασή μου",
			available: "διαθέσιμος",
			busy: "απασχολημένος",
			away: "απών",
			atMost: "το πολύ",
			noCeiling: "χωρίς ανώτατο όριο",
			ceilingLabel: "Ανώτατο όριο ταυτόχρονων συνομιλιών",
			conversations: "{n} συνομιλία | {n} συνομιλίες",
			conversationsOf: "{n} συνομιλία από {max} | {n} συνομιλίες από {max}",
		},
		signature: {
			lead: "Αυτό που διαβάζει ο πελάτης πριν από τα λόγια, όταν απαντά άνθρωπος. Τα μηνύματα του εκπροσώπου και των αυτοματισμών δεν υπογράφονται — όποιος θέλει ο εκπρόσωπος να συστήνεται, το λέει στην περσόνα του.",
			none: "Χωρίς υπογραφή",
			noneHint: "Μόνο τα λόγια, όπως ήταν πάντα.",
			name: "Όνομα όποιου απαντά",
			nameHint: "Όποιος είναι στο πληκτρολόγιο αυτή τη στιγμή.",
			titleOnly: "Μόνο ο τίτλος",
			titleOnlyHint: "Η ομάδα μιλά με μία φωνή.",
			nameTitle: "Όνομα και τίτλος",
			nameTitleHint: "Και τα δύο, χωρισμένα με παύλα.",
			title: "Υπογραφή στις απαντήσεις",
			titleLabel: "Τίτλος",
			titlePlaceholder: "Εκπρόσωπος",
			howItReads: "Πώς θα το διαβάσει ο πελάτης",
			sample: "Γεια! Έχω ήδη ξεχωρίσει την παραγγελία σου, φτάνει την Πέμπτη.",
			noSignature: "Χωρίς υπογραφή: ο πελάτης δεν μαθαίνει ποιος απάντησε.",
			adminOnly: "Μόνο ένας διαχειριστής το αλλάζει αυτό.",
			failed: "δεν ήταν δυνατή η αποθήκευση",
		},
		teams: {
			title: "Ομάδες",
			subtitle: "Ομάδες στις οποίες μπορεί να μεταφερθεί μια συνομιλία",
			notAccess:
				"Οι ομάδες οργανώνουν την ουρά εξυπηρέτησης και τις ειδοποιήσεις. Δεν αλλάζουν τι βλέπει κάθε άτομο — γι' αυτό χρησιμοποιήστε τα Προφίλ πρόσβασης στις Ρυθμίσεις → Πρόσβαση.",
			notAccessLink: "Άνοιγμα Πρόσβασης",
			empty: "Καμία ομάδα ακόμη. Μια συνομιλία που μεταφέρεται χωρίς ομάδα μένει στη γενική ουρά.",
			members: "{n} άτομο | {n} άτομα",
			whoIsIn: "ποιος συμμετέχει",
			failed: "δεν έγινε",
			itIsYou: "είσαι εσύ",
			soleOwner: "ο μόνος ενεργός ιδιοκτήτης",
			neverSignedIn: "δεν έχει συνδεθεί ποτέ",
			error: {
				lastOwner:
					"Αυτός ο χώρος εργασίας χρειάζεται τουλάχιστον έναν ενεργό ιδιοκτήτη. Προήγαγε πρώτα κάποιον άλλον.",
				forbidden: "Μόνο ένας διαχειριστής μπορεί να το κάνει αυτό.",
				notFound: "Αυτό το άτομο δεν είναι πια σε αυτόν τον χώρο εργασίας.",
				network: "Δεν ήταν δυνατή η επικοινωνία με τον διακομιστή.",
				failed: "Δεν ήταν δυνατή η αποθήκευση αυτής της αλλαγής.",
			},
			now: "τώρα",
			minutesAgo: "πριν {n} λ.",
			hoursAgo: "πριν {n} ώ.",
			daysAgo: "πριν {n} ημέρα | πριν {n} ημέρες",
		},
		account: {
			title: "Εσύ",
			subtitle: "Το όνομά σου, ο ρόλος σου και ο κωδικός σου",
			photoReplace: "Αντικατάσταση φωτογραφίας",
			photoUpload: "Μεταφόρτωση φωτογραφίας",
			photoFailed: "δεν ήταν δυνατή η μεταφόρτωση της φωτογραφίας",
			photoRemoveFailed: "δεν ήταν δυνατή η αφαίρεση της φωτογραφίας",
			failed: "δεν ήταν δυνατή η αποθήκευση",
			wrongPassword: "ο τρέχων κωδικός δεν ταιριάζει",
			passwordFailed: "δεν ήταν δυνατή η αλλαγή του κωδικού",
			phoneInvalid:
				"Ξεκίνα με + και τον κωδικό χώρας — χωρίς αυτόν ο αριθμός είναι διφορούμενος ανάμεσα σε χώρες.",
			phoneHint:
				"Από εδώ σε φτάνει μια ειδοποίηση εκτός του πάνελ. Άφησέ το κενό για να μη λαμβάνεις τίποτα.",
			emailNote: "E-mail σύνδεσης: {email}",
			passwordNote:
				"Με την αλλαγή, όλες οι συνεδρίες σου πέφτουν — συμπεριλαμβανομένης αυτής. Ξαναμπαίνεις με τον νέο κωδικό.",
			yourName: "Το όνομά σου",
			yourWhatsapp: "Το WhatsApp σου",
			saved: "Αποθηκεύτηκε.",
			changePassword: "Αλλαγή κωδικού",
			currentPassword: "Τρέχων κωδικός",
			newPassword: "Νέος κωδικός",
			changeEmail: "Αλλαγή e-mail σύνδεσης",
			newEmail: "Νέο e-mail",
			sendLink: "Αποστολή συνδέσμου επιβεβαίωσης",
			emailSent:
				"Στείλαμε σύνδεσμο στο {email}. Άνοιξέ τον για επιβεβαίωση — ως τότε συνδέεσαι με το τρέχον e-mail.",
			emailTaken: "Αυτό το e-mail χρησιμοποιείται ήδη από άλλο λογαριασμό.",
			emailInvalid: "Αυτό το e-mail δεν φαίνεται έγκυρο.",
			emailSame: "Αυτό είναι ήδη το e-mail σου.",
			emailFailed: "δεν ήταν δυνατό να ζητηθεί η αλλαγή",
		},
		error: {
			forbidden: "Μόνο ένας διαχειριστής μπορεί να το κάνει αυτό.",
			notFound: "Αυτό το άτομο δεν είναι πια σε αυτόν τον χώρο εργασίας.",
			offline: "Δεν ήταν δυνατή η επικοινωνία με τον διακομιστή.",
			generic: "Δεν ήταν δυνατή η αποθήκευση αυτής της αλλαγής.",
		},
	},
	es: {
		prefs: {
			title: "Preferencias",
			subtitle: "El nombre del espacio de trabajo y cómo lee fechas, horas y dinero",
			name: "Nombre del espacio de trabajo",
			timezone: "Zona horaria",
			timezoneHint:
				"Decide qué significa “hoy” en los informes y a qué hora se ejecutan las programaciones.",
			language: "Idioma",
			languageHint: "Lo que este negocio habla con sus clientes.",
			currency: "Moneda",
			country: "País",
			countryHint: "Define por qué validador pasa un documento nacional.",
			previewTitle: "Cómo se verá",
			previewWhen: "Fecha y hora",
			previewMoney: "Importe",
			adminOnly: "Solo un administrador cambia las preferencias del espacio de trabajo.",
			saved: "Preferencias guardadas.",
			failed: "no se pudo guardar",
		},
		agentDisplay: {
			title: "Qué se muestra mientras trabaja un agente",
			lead: "Se aplica a todo el mundo en este espacio de trabajo. No cambia lo que el agente HACE, solo lo que el equipo ve mientras lo hace.",
			reasoning: "Mostrar el razonamiento",
			reasoningHint:
				"Lo que pensó antes de responder, en un bloque que se abre. Útil para ajustar la persona; ruido para quien solo quiere la respuesta.",
			tools: "Mostrar las herramientas",
			toolsHint:
				"Qué herramientas usó, con entrada y salida. Así se descubre por qué una respuesta salió mal.",
			cost: "Mostrar el consumo",
			costHint: "Los créditos consumidos por cada respuesta.",
			adminOnly: "Solo un administrador cambia esto.",
			failed: "No se pudo guardar ese cambio.",
		},
		brand: {
			loadFailed: "No se pudo cargar la identidad visual.",
			identityHint:
				"Identidad visual que Ben usa en sitios, pantallas y materiales. El comportamiento se define en las reglas del espacio o de cada agente.",
			slogan: "Eslogan",
			typography: "Tipografía",
			undecided: "Definir después",
			sans: "Sin serifa",
			serif: "Con serifa",
			mono: "Monoespaciada",
			colors: "Colores",
			colorsHint: "Paleta ordenada: color principal primero.",
			color: "Color {n}",
			colorCode: "Código hexadecimal {n}",
			removeColor: "Eliminar color",
			addColor: "Añadir color",

			title: "Marca",
			subtitle: "El logotipo que aparece en el panel, en la web publicada y en los formularios",
			uploadFailed: "No se pudo subir la imagen ahora mismo.",
			sendFailed: "no se pudo subir la imagen",
			removeFailed: "no se pudo quitar",
			hint: "PNG, JPG, SVG o WebP, hasta 2 MB. Con fondo transparente queda mejor: el logotipo aparece sobre claro y sobre oscuro. Quien abre la web o el formulario publicado ve esta imagen.",
			adminOnly: "Solo un administrador cambia la marca del espacio de trabajo.",
			updated: "Marca actualizada.",
			removed: "Marca eliminada.",
			tooBig: "La imagen pasa de 2 MB. Un logotipo cabe de sobra por debajo.",
			replace: "Sustituir",
			upload: "Subir logotipo",
		},
		business: {
			title: "El negocio",
			subtitle:
				"Lo que ya sabe todo agente de este espacio de trabajo, antes de la persona de cada uno",
			saved: "El negocio se guardó.",
			failed: "no se pudo guardar",
			placeholder: "Sector, qué se vende, a quién, qué es importante saber.",
			boundaryHint:
				"Una restricción, no contexto: llega al agente como un bloque propio, antes de la persona.",
			logoAlt: "Logotipo del negocio",
			logoCaption: "Capturado durante la puesta en marcha. Sustitúyelo en Marca.",
			whatLabel: "Qué hace la empresa",
			whatHint: "Contexto. Vale para todos los agentes, sin repetirlo en cada persona.",
			rulesLabel: "Reglas fijas",
			rulesPlaceholder: "Lo que ningún agente puede decir, prometer ni garantizar.",
		},
		company: {
			subtitle: "Lo que un contrato, un presupuesto o un recibo imprime sobre quien lo emite",
			legalName: "Razón social",
			legalNameHint:
				"El nombre con el que se emite un documento, cuando no coincide con el del espacio de trabajo.",
			taxIdHint:
				"Se guarda tal como lo escribas. El nombre del campo viene del país del espacio de trabajo.",
			taxId: "Identificación fiscal",
			taxIdNamed: "Identificación fiscal ({local})",
			emailInvalid: "Falta la arroba: es adonde un cliente responde al documento.",
			phoneHint:
				"Cómo debe imprimirse. No es el número que recibe avisos: ese es el tuyo, en “Tú”.",
			adminOnly: "Solo un administrador cambia los datos de la empresa.",
			failed: "no se pudo guardar",
			line1: "Dirección",
			line1Placeholder: "Calle, avenida, número",
			region: "Región / provincia",
			postalCode: "Código postal",
			country: "País",
			title: "La empresa",
			line2: "Segunda línea",
			line2Placeholder: "Puerta, planta, bloque",
			city: "Ciudad",
			contactEmail: "Correo de contacto",
			phone: "Teléfono",
		},
		presence: {
			mine: "Mi presencia",
			state: "Mi estado",
			available: "disponible",
			busy: "ocupado",
			away: "ausente",
			atMost: "como máximo",
			noCeiling: "sin límite",
			ceilingLabel: "Límite de conversaciones simultáneas",
			conversations: "{n} conversación | {n} conversaciones",
			conversationsOf: "{n} conversación de {max} | {n} conversaciones de {max}",
		},
		signature: {
			lead: "Lo que el cliente lee antes de las palabras, cuando quien responde es una persona. Los mensajes del agente de atención y de las automatizaciones no van firmados: quien quiera que el agente de atención se presente lo dice en su persona.",
			none: "No firmar",
			noneHint: "Solo las palabras, como siempre.",
			name: "Nombre de quien responde",
			nameHint: "Quien está al teclado ahora mismo.",
			titleOnly: "Solo el cargo",
			titleOnlyHint: "El equipo habla con una sola voz.",
			nameTitle: "Nombre y cargo",
			nameTitleHint: "Los dos, separados por un guion.",
			title: "Firma en las respuestas",
			titleLabel: "Cargo",
			titlePlaceholder: "Agente de atención",
			howItReads: "Cómo lo leerá el cliente",
			sample: "¡Hola! Ya he apartado tu pedido, llega el jueves.",
			noSignature: "Sin firma: el cliente no llega a saber quién respondió.",
			adminOnly: "Solo un administrador cambia esto.",
			failed: "no se pudo guardar",
		},
		teams: {
			title: "Equipos",
			subtitle: "Grupos a los que se puede transferir una conversación",
			notAccess:
				"Los equipos organizan la cola de atención y los avisos. No cambian lo que ve cada persona — para eso usa Perfiles de acceso en Ajustes → Accesos.",
			notAccessLink: "Abrir Accesos",
			empty:
				"Aún no hay ningún equipo. Una conversación transferida sin equipo se queda en la cola general.",
			members: "{n} persona | {n} personas",
			whoIsIn: "quién está",
			failed: "no hubo suerte",
			itIsYou: "eres tú",
			soleOwner: "el único propietario activo",
			neverSignedIn: "nunca ha entrado",
			error: {
				lastOwner:
					"Este espacio de trabajo necesita al menos un propietario activo. Asciende antes a otra persona.",
				forbidden: "Solo un administrador puede hacer eso.",
				notFound: "Esa persona ya no está en este espacio de trabajo.",
				network: "No se pudo contactar con el servidor.",
				failed: "No se pudo guardar ese cambio.",
			},
			now: "ahora",
			minutesAgo: "hace {n} min",
			hoursAgo: "hace {n} h",
			daysAgo: "hace {n} día | hace {n} días",
		},
		account: {
			title: "Tú",
			subtitle: "Tu nombre, tu rol y tu contraseña",
			photoReplace: "Cambiar foto",
			photoUpload: "Subir foto",
			photoFailed: "no se pudo subir la foto",
			photoRemoveFailed: "no se pudo quitar la foto",
			failed: "no se pudo guardar",
			wrongPassword: "la contraseña actual no coincide",
			passwordFailed: "no se pudo cambiar la contraseña",
			phoneInvalid:
				"Empieza con + y el prefijo del país: sin él el número es ambiguo entre países.",
			phoneHint:
				"Es por aquí donde un aviso te alcanza fuera del panel. Déjalo en blanco para no recibir nada.",
			emailNote: "E-mail de acceso: {email}",
			passwordNote:
				"Al cambiarla, todas tus sesiones se cierran, incluida esta. Vuelves a entrar con la contraseña nueva.",
			yourName: "Tu nombre",
			yourWhatsapp: "Tu WhatsApp",
			saved: "Guardado.",
			changePassword: "Cambiar la contraseña",
			currentPassword: "Contraseña actual",
			newPassword: "Contraseña nueva",
			changeEmail: "Cambiar el e-mail de acceso",
			newEmail: "E-mail nuevo",
			sendLink: "Enviar enlace de confirmación",
			emailSent:
				"Enviamos un enlace a {email}. Ábrelo para confirmar — hasta entonces sigues entrando con el e-mail actual.",
			emailTaken: "Este e-mail ya lo usa otra cuenta.",
			emailInvalid: "Este e-mail no parece válido.",
			emailSame: "Este ya es tu e-mail.",
			emailFailed: "no se pudo pedir el cambio",
		},
		error: {
			forbidden: "Solo un administrador puede hacer eso.",
			notFound: "Esa persona ya no está en este espacio de trabajo.",
			offline: "No se pudo contactar con el servidor.",
			generic: "No se pudo guardar ese cambio.",
		},
	},
	fr: {
		prefs: {
			title: "Préférences",
			subtitle:
				"Le nom de l'espace de travail, et comment il lit les dates, les heures et l'argent",
			name: "Nom de l'espace de travail",
			timezone: "Fuseau horaire",
			timezoneHint:
				"Décide de ce que « aujourd'hui » veut dire dans les rapports et à quelle heure les planifications tournent.",
			language: "Langue",
			languageHint: "Ce que cette entreprise parle avec ses clients.",
			currency: "Devise",
			country: "Pays",
			countryHint: "Définit par quel contrôle passe un document national.",
			previewTitle: "Ce que ça donnera",
			previewWhen: "Date et heure",
			previewMoney: "Montant",
			adminOnly: "Seul un administrateur modifie les préférences de l'espace de travail.",
			saved: "Préférences enregistrées.",
			failed: "impossible d'enregistrer",
		},
		agentDisplay: {
			title: "Ce qui s'affiche pendant qu'un agent travaille",
			lead: "Cela vaut pour tout le monde dans cet espace de travail. Cela ne change pas ce que l'agent FAIT — seulement ce que l'équipe voit pendant qu'il le fait.",
			reasoning: "Afficher le raisonnement",
			reasoningHint:
				"Ce qu'il a pensé avant de répondre, dans un bloc qui s'ouvre. Utile pour régler la persona ; du bruit pour qui veut juste la réponse.",
			tools: "Afficher les outils",
			toolsHint:
				"Quels outils il a utilisés, avec l'entrée et la sortie. C'est comme ça qu'on découvre pourquoi une réponse est sortie fausse.",
			cost: "Afficher la consommation",
			costHint: "Les crédits consommés par chaque réponse.",
			adminOnly: "Seul un administrateur modifie ceci.",
			failed: "Impossible d'enregistrer ce changement.",
		},
		brand: {
			loadFailed: "Impossible de charger l’identité visuelle.",
			identityHint:
				"Identité visuelle utilisée par Ben pour les sites, écrans et supports. Les règles de comportement restent séparées.",
			slogan: "Slogan",
			typography: "Typographie",
			undecided: "Définir plus tard",
			sans: "Sans empattement",
			serif: "Avec empattement",
			mono: "Chasse fixe",
			colors: "Couleurs",
			colorsHint: "Palette ordonnée : couleur principale en premier.",
			color: "Couleur {n}",
			colorCode: "Code hexadécimal {n}",
			removeColor: "Retirer la couleur",
			addColor: "Ajouter une couleur",

			title: "Marque",
			subtitle: "Le logo qui apparaît dans le panneau, sur le site publié et sur les formulaires",
			uploadFailed: "Impossible d'envoyer l'image pour le moment.",
			sendFailed: "impossible d'envoyer l'image",
			removeFailed: "impossible de la retirer",
			hint: "PNG, JPG, SVG ou WebP, jusqu'à 2 Mo. Un fond transparent rend mieux : le logo apparaît sur clair et sur sombre. Qui ouvre le site ou le formulaire publié voit cette image.",
			adminOnly: "Seul un administrateur change la marque de l'espace de travail.",
			updated: "Marque mise à jour.",
			removed: "Marque retirée.",
			tooBig: "L'image dépasse 2 Mo. Un logo tient largement en dessous.",
			replace: "Remplacer",
			upload: "Envoyer le logo",
		},
		business: {
			title: "L'activité",
			subtitle:
				"Ce que chaque agent de cet espace de travail sait déjà, avant la persona de chacun",
			saved: "L'activité a été enregistrée.",
			failed: "impossible d'enregistrer",
			placeholder: "Secteur, ce qui est vendu, à qui, ce qu'il est important de savoir.",
			boundaryHint:
				"Une contrainte, pas du contexte — cela arrive à l'agent dans un bloc à part, avant la persona.",
			logoAlt: "Logo de l'activité",
			logoCaption: "Récupéré pendant la prise en main. Remplacez-le dans Marque.",
			whatLabel: "Ce que fait l'entreprise",
			whatHint: "Du contexte. Cela vaut pour tous les agents, sans le répéter dans chaque persona.",
			rulesLabel: "Règles fixes",
			rulesPlaceholder: "Ce qu'aucun agent ne peut dire, promettre ou garantir.",
		},
		company: {
			subtitle: "Ce qu'un contrat, un devis ou un reçu imprime sur celui qui l'émet",
			legalName: "Raison sociale",
			legalNameHint:
				"Le nom sous lequel un document est émis, quand il diffère du nom de l'espace de travail.",
			taxIdHint:
				"Enregistré tel que vous l'écrivez. Le nom du champ vient du pays de l'espace de travail.",
			taxId: "Numéro fiscal",
			taxIdNamed: "Numéro fiscal ({local})",
			emailInvalid: "L'arobase manque — c'est là qu'un client répond au document.",
			phoneHint:
				"Comment il doit s'imprimer. Ce n'est pas le numéro qui reçoit les avis — celui-là est le vôtre, dans « Vous ».",
			adminOnly: "Seul un administrateur modifie les informations légales de l'entreprise.",
			failed: "impossible d'enregistrer",
			line1: "Adresse",
			line1Placeholder: "Rue, avenue, numéro",
			region: "Région / département",
			postalCode: "Code postal",
			country: "Pays",
			title: "L'entreprise",
			line2: "Deuxième ligne",
			line2Placeholder: "Bureau, étage, bâtiment",
			city: "Ville",
			contactEmail: "E-mail de contact",
			phone: "Téléphone",
		},
		presence: {
			mine: "Ma présence",
			state: "Mon état",
			available: "disponible",
			busy: "occupé",
			away: "absent",
			atMost: "au plus",
			noCeiling: "sans plafond",
			ceilingLabel: "Plafond de conversations simultanées",
			conversations: "{n} conversation | {n} conversations",
			conversationsOf: "{n} conversation sur {max} | {n} conversations sur {max}",
		},
		signature: {
			lead: "Ce que le client lit avant les mots, quand c'est une personne qui répond. Les messages du conseiller et des automatisations ne sont pas signés — qui veut que le conseiller se présente le dit dans sa persona.",
			none: "Ne pas signer",
			noneHint: "Juste les mots, comme depuis toujours.",
			name: "Nom de qui répond",
			nameHint: "Qui est au clavier en ce moment.",
			titleOnly: "Le titre seul",
			titleOnlyHint: "L'équipe parle d'une seule voix.",
			nameTitle: "Nom et titre",
			nameTitleHint: "Les deux, séparés par un tiret.",
			title: "Signature sur les réponses",
			titleLabel: "Titre",
			titlePlaceholder: "Conseiller",
			howItReads: "Ce que le client va lire",
			sample: "Bonjour ! J'ai déjà mis votre commande de côté, elle arrive jeudi.",
			noSignature: "Sans signature : le client ne saura pas qui a répondu.",
			adminOnly: "Seul un administrateur modifie ceci.",
			failed: "impossible d'enregistrer",
		},
		teams: {
			title: "Équipes",
			subtitle: "Groupes vers lesquels une conversation peut être transférée",
			notAccess:
				"Les équipes organisent la file d'attente et les notifications. Elles ne changent pas ce que chaque personne voit — pour cela, utilisez les Profils d'accès dans Paramètres → Accès.",
			notAccessLink: "Ouvrir Accès",
			empty:
				"Aucune équipe pour l'instant. Une conversation transférée sans équipe reste dans la file générale.",
			members: "{n} personne | {n} personnes",
			whoIsIn: "qui en fait partie",
			failed: "raté",
			itIsYou: "c'est vous",
			soleOwner: "le seul propriétaire actif",
			neverSignedIn: "jamais connecté",
			error: {
				lastOwner:
					"Cet espace de travail a besoin d'au moins un propriétaire actif. Promouvez d'abord quelqu'un d'autre.",
				forbidden: "Seul un administrateur peut faire cela.",
				notFound: "Cette personne n'est plus dans cet espace de travail.",
				network: "Impossible de joindre le serveur.",
				failed: "Impossible d'enregistrer ce changement.",
			},
			now: "à l'instant",
			minutesAgo: "il y a {n} min",
			hoursAgo: "il y a {n} h",
			daysAgo: "il y a {n} jour | il y a {n} jours",
		},
		account: {
			title: "Vous",
			subtitle: "Votre nom, votre rôle et votre mot de passe",
			photoReplace: "Remplacer la photo",
			photoUpload: "Envoyer une photo",
			photoFailed: "impossible d'envoyer la photo",
			photoRemoveFailed: "impossible de retirer la photo",
			failed: "impossible d'enregistrer",
			wrongPassword: "le mot de passe actuel ne correspond pas",
			passwordFailed: "impossible de changer le mot de passe",
			phoneInvalid:
				"Commencez par + et l'indicatif du pays — sans lui, le numéro est ambigu d'un pays à l'autre.",
			phoneHint:
				"C'est par là qu'un avis vous atteint hors du panneau. Laissez vide pour ne rien recevoir.",
			emailNote: "E-mail de connexion : {email}",
			passwordNote:
				"En le changeant, toutes vos sessions tombent — celle-ci comprise. Vous revenez avec le nouveau mot de passe.",
			yourName: "Votre nom",
			yourWhatsapp: "Votre WhatsApp",
			saved: "Enregistré.",
			changePassword: "Changer le mot de passe",
			currentPassword: "Mot de passe actuel",
			newPassword: "Nouveau mot de passe",
			changeEmail: "Changer l'e-mail de connexion",
			newEmail: "Nouvel e-mail",
			sendLink: "Envoyer le lien de confirmation",
			emailSent:
				"Nous avons envoyé un lien à {email}. Ouvrez-le pour confirmer — en attendant, vous vous connectez avec l'e-mail actuel.",
			emailTaken: "Cet e-mail est déjà utilisé par un autre compte.",
			emailInvalid: "Cet e-mail ne semble pas valide.",
			emailSame: "C'est déjà votre e-mail.",
			emailFailed: "impossible de demander le changement",
		},
		error: {
			forbidden: "Seul un administrateur peut faire cela.",
			notFound: "Cette personne n'est plus dans cet espace de travail.",
			offline: "Impossible de joindre le serveur.",
			generic: "Impossible d'enregistrer ce changement.",
		},
	},
	it: {
		prefs: {
			title: "Preferenze",
			subtitle: "Il nome dello spazio di lavoro e come legge date, orari e denaro",
			name: "Nome dello spazio di lavoro",
			timezone: "Fuso orario",
			timezoneHint:
				"Decide che cosa significa “oggi” nei report e a che ora partono le pianificazioni.",
			language: "Lingua",
			languageHint: "Quello che questa attività parla con i suoi clienti.",
			currency: "Valuta",
			country: "Paese",
			countryHint: "Stabilisce da quale controllo passa un documento nazionale.",
			previewTitle: "Come apparirà",
			previewWhen: "Data e ora",
			previewMoney: "Importo",
			adminOnly: "Solo un amministratore cambia le preferenze dello spazio di lavoro.",
			saved: "Preferenze salvate.",
			failed: "non è stato possibile salvare",
		},
		agentDisplay: {
			title: "Che cosa si vede mentre un agente lavora",
			lead: "Vale per tutti in questo spazio di lavoro. Non cambia quello che l'agente FA — solo quello che il team vede mentre lo fa.",
			reasoning: "Mostra il ragionamento",
			reasoningHint:
				"Che cosa ha pensato prima di rispondere, in un blocco che si apre. Utile per mettere a punto la persona; rumore per chi vuole solo la risposta.",
			tools: "Mostra gli strumenti",
			toolsHint:
				"Quali strumenti ha usato, con ingresso e uscita. È così che scopri perché una risposta è uscita sbagliata.",
			cost: "Mostra il consumo",
			costHint: "I crediti consumati da ogni risposta.",
			adminOnly: "Solo un amministratore cambia questo.",
			failed: "Non è stato possibile salvare questa modifica.",
		},
		brand: {
			loadFailed: "Impossibile caricare l’identità visiva.",
			identityHint:
				"Identità visiva usata da Ben per siti, schermate e materiali. Le regole di comportamento restano separate.",
			slogan: "Slogan",
			typography: "Tipografia",
			undecided: "Definisci dopo",
			sans: "Senza grazie",
			serif: "Con grazie",
			mono: "Monospazio",
			colors: "Colori",
			colorsHint: "Tavolozza ordinata: prima il colore principale.",
			color: "Colore {n}",
			colorCode: "Codice esadecimale {n}",
			removeColor: "Rimuovi colore",
			addColor: "Aggiungi colore",

			title: "Marchio",
			subtitle: "Il logo che compare nel pannello, sul sito pubblicato e sui moduli",
			uploadFailed: "Non è stato possibile caricare l'immagine adesso.",
			sendFailed: "non è stato possibile caricare l'immagine",
			removeFailed: "non è stato possibile rimuoverlo",
			hint: "PNG, JPG, SVG o WebP, fino a 2 MB. Con lo sfondo trasparente viene meglio: il logo compare sul chiaro e sullo scuro. Chi apre il sito o il modulo pubblicato vede questa immagine.",
			adminOnly: "Solo un amministratore cambia il marchio dello spazio di lavoro.",
			updated: "Marchio aggiornato.",
			removed: "Marchio rimosso.",
			tooBig: "L'immagine supera i 2 MB. Un logo ci sta comodamente sotto.",
			replace: "Sostituisci",
			upload: "Carica il logo",
		},
		business: {
			title: "L'attività",
			subtitle:
				"Quello che ogni agente di questo spazio di lavoro sa già, prima della persona di ciascuno",
			saved: "L'attività è stata salvata.",
			failed: "non è stato possibile salvare",
			placeholder: "Settore, che cosa si vende, a chi, che cosa è importante sapere.",
			boundaryHint:
				"Un vincolo, non contesto — arriva all'agente come blocco a sé, prima della persona.",
			logoAlt: "Logo dell'attività",
			logoCaption: "Raccolto durante l'onboarding. Sostituiscilo in Marchio.",
			whatLabel: "Che cosa fa l'azienda",
			whatHint: "Contesto. Vale per tutti gli agenti, senza ripeterlo in ogni persona.",
			rulesLabel: "Regole fisse",
			rulesPlaceholder: "Quello che nessun agente può dire, promettere o garantire.",
		},
		company: {
			subtitle: "Quello che un contratto, un preventivo o una ricevuta stampa su chi lo emette",
			legalName: "Ragione sociale",
			legalNameHint:
				"Il nome con cui viene emesso un documento, quando è diverso da quello dello spazio di lavoro.",
			taxIdHint:
				"Salvato come lo scrivi. Il nome del campo viene dal paese dello spazio di lavoro.",
			taxId: "Codice fiscale",
			taxIdNamed: "Codice fiscale ({local})",
			emailInvalid: "Manca la chiocciola — è lì che un cliente risponde al documento.",
			phoneHint:
				"Come deve essere stampato. Non è il numero che riceve gli avvisi — quello è il tuo, in “Tu”.",
			adminOnly: "Solo un amministratore cambia i dati dell'azienda.",
			failed: "non è stato possibile salvare",
			line1: "Indirizzo",
			line1Placeholder: "Via, viale, numero",
			region: "Regione / provincia",
			postalCode: "CAP",
			country: "Paese",
			title: "L'azienda",
			line2: "Seconda riga",
			line2Placeholder: "Interno, piano, scala",
			city: "Città",
			contactEmail: "E-mail di contatto",
			phone: "Telefono",
		},
		presence: {
			mine: "La mia presenza",
			state: "Il mio stato",
			available: "disponibile",
			busy: "occupato",
			away: "assente",
			atMost: "al massimo",
			noCeiling: "senza tetto",
			ceilingLabel: "Tetto di conversazioni in parallelo",
			conversations: "{n} conversazione | {n} conversazioni",
			conversationsOf: "{n} conversazione su {max} | {n} conversazioni su {max}",
		},
		signature: {
			lead: "Quello che il cliente legge prima delle parole, quando a rispondere è una persona. I messaggi dell'operatore e delle automazioni non vengono firmati — chi vuole che l'operatore si presenti lo scrive nella sua persona.",
			none: "Non firmare",
			noneHint: "Solo le parole, come è sempre stato.",
			name: "Nome di chi risponde",
			nameHint: "Chi è alla tastiera in questo momento.",
			titleOnly: "Solo la qualifica",
			titleOnlyHint: "Il team parla con una voce sola.",
			nameTitle: "Nome e qualifica",
			nameTitleHint: "Tutti e due, separati da un trattino.",
			title: "Firma nelle risposte",
			titleLabel: "Qualifica",
			titlePlaceholder: "Operatore",
			howItReads: "Come lo leggerà il cliente",
			sample: "Ciao! Ho già messo da parte il tuo ordine, arriva giovedì.",
			noSignature: "Senza firma: il cliente non viene a sapere chi ha risposto.",
			adminOnly: "Solo un amministratore cambia questo.",
			failed: "non è stato possibile salvare",
		},
		teams: {
			title: "Team",
			subtitle: "Gruppi a cui una conversazione può essere trasferita",
			notAccess:
				"I team organizzano la coda di assistenza e gli avvisi. Non cambiano ciò che ogni persona vede — per questo usa i Profili di accesso in Impostazioni → Accessi.",
			notAccessLink: "Apri Accessi",
			empty:
				"Ancora nessun team. Una conversazione trasferita senza team resta nella coda generale.",
			members: "{n} persona | {n} persone",
			whoIsIn: "chi c'è",
			failed: "niente da fare",
			itIsYou: "sei tu",
			soleOwner: "l'unico proprietario attivo",
			neverSignedIn: "mai entrato",
			error: {
				lastOwner:
					"Questo spazio di lavoro ha bisogno di almeno un proprietario attivo. Promuovi prima qualcun altro.",
				forbidden: "Solo un amministratore può farlo.",
				notFound: "Questa persona non è più in questo spazio di lavoro.",
				network: "Non è stato possibile raggiungere il server.",
				failed: "Non è stato possibile salvare questa modifica.",
			},
			now: "adesso",
			minutesAgo: "{n} min fa",
			hoursAgo: "{n} h fa",
			daysAgo: "{n} giorno fa | {n} giorni fa",
		},
		account: {
			title: "Tu",
			subtitle: "Il tuo nome, il tuo ruolo e la tua password",
			photoReplace: "Sostituisci foto",
			photoUpload: "Carica foto",
			photoFailed: "non è stato possibile caricare la foto",
			photoRemoveFailed: "non è stato possibile rimuovere la foto",
			failed: "non è stato possibile salvare",
			wrongPassword: "la password attuale non corrisponde",
			passwordFailed: "non è stato possibile cambiare la password",
			phoneInvalid:
				"Comincia con + e il prefisso del paese — senza, il numero è ambiguo tra paesi.",
			phoneHint:
				"È da qui che un avviso ti raggiunge fuori dal pannello. Lascia vuoto per non ricevere niente.",
			emailNote: "E-mail di accesso: {email}",
			passwordNote:
				"Cambiandola, tutte le tue sessioni cadono — questa compresa. Rientri con la password nuova.",
			yourName: "Il tuo nome",
			yourWhatsapp: "Il tuo WhatsApp",
			saved: "Salvato.",
			changePassword: "Cambia la password",
			currentPassword: "Password attuale",
			newPassword: "Password nuova",
			changeEmail: "Cambia l'e-mail di accesso",
			newEmail: "Nuova e-mail",
			sendLink: "Invia link di conferma",
			emailSent:
				"Abbiamo inviato un link a {email}. Aprilo per confermare — fino ad allora accedi con l'e-mail attuale.",
			emailTaken: "Questa e-mail è già usata da un altro account.",
			emailInvalid: "Questa e-mail non sembra valida.",
			emailSame: "Questa è già la tua e-mail.",
			emailFailed: "non è stato possibile richiedere il cambio",
		},
		error: {
			forbidden: "Solo un amministratore può farlo.",
			notFound: "Questa persona non è più in questo spazio di lavoro.",
			offline: "Non è stato possibile raggiungere il server.",
			generic: "Non è stato possibile salvare questa modifica.",
		},
	},
	de: {
		prefs: {
			title: "Voreinstellungen",
			subtitle: "Der Name des Workspace und wie er Datum, Uhrzeit und Geld liest",
			name: "Name des Workspace",
			timezone: "Zeitzone",
			timezoneHint: "Legt fest, was „heute“ in Berichten bedeutet und wann Zeitpläne laufen.",
			language: "Sprache",
			languageHint: "Was dieses Unternehmen mit seinen Kunden spricht.",
			currency: "Währung",
			country: "Land",
			countryHint: "Legt fest, durch welche Prüfung ein nationales Dokument läuft.",
			previewTitle: "So wird es aussehen",
			previewWhen: "Datum und Uhrzeit",
			previewMoney: "Betrag",
			adminOnly: "Nur ein Administrator ändert die Voreinstellungen des Workspace.",
			saved: "Voreinstellungen gespeichert.",
			failed: "konnte nicht gespeichert werden",
		},
		agentDisplay: {
			title: "Was zu sehen ist, während ein Agent arbeitet",
			lead: "Gilt für alle in diesem Workspace. Es ändert nicht, was der Agent TUT — nur, was das Team dabei sieht.",
			reasoning: "Denkweg anzeigen",
			reasoningHint:
				"Was er vor der Antwort gedacht hat, in einem aufklappbaren Block. Nützlich zum Feinschliff der Persona; Lärm für alle, die nur die Antwort wollen.",
			tools: "Werkzeuge anzeigen",
			toolsHint:
				"Welche Werkzeuge er benutzt hat, mit Eingabe und Ausgabe. So findet man heraus, warum eine Antwort falsch herauskam.",
			cost: "Verbrauch anzeigen",
			costHint: "Die verbrauchten Credits jeder Antwort.",
			adminOnly: "Nur ein Administrator ändert das.",
			failed: "Diese Änderung konnte nicht gespeichert werden.",
		},
		brand: {
			loadFailed: "Die visuelle Identität konnte nicht geladen werden.",
			identityHint:
				"Visuelle Identität für Bens Websites, Ansichten und Materialien. Verhaltensregeln werden separat festgelegt.",
			slogan: "Slogan",
			typography: "Typografie",
			undecided: "Später festlegen",
			sans: "Serifenlos",
			serif: "Serifenschrift",
			mono: "Festbreitenschrift",
			colors: "Farben",
			colorsHint: "Geordnete Palette: Hauptfarbe zuerst.",
			color: "Farbe {n}",
			colorCode: "Hex-Code {n}",
			removeColor: "Farbe entfernen",
			addColor: "Farbe hinzufügen",

			title: "Marke",
			subtitle:
				"Das Logo, das im Panel, auf der veröffentlichten Website und auf den Formularen erscheint",
			uploadFailed: "Das Bild konnte gerade nicht hochgeladen werden.",
			sendFailed: "das Bild konnte nicht hochgeladen werden",
			removeFailed: "es konnte nicht entfernt werden",
			hint: "PNG, JPG, SVG oder WebP, bis 2 MB. Ein transparenter Hintergrund wirkt besser: Das Logo erscheint auf Hell und auf Dunkel. Wer die Website oder das veröffentlichte Formular öffnet, sieht dieses Bild.",
			adminOnly: "Nur ein Administrator ändert die Marke des Workspace.",
			updated: "Marke aktualisiert.",
			removed: "Marke entfernt.",
			tooBig: "Das Bild ist über 2 MB. Ein Logo passt gut darunter.",
			replace: "Ersetzen",
			upload: "Logo hochladen",
		},
		business: {
			title: "Das Geschäft",
			subtitle:
				"Was jeder Agent in diesem Workspace schon weiß, noch vor der Persona des Einzelnen",
			saved: "Das Geschäft wurde gespeichert.",
			failed: "konnte nicht gespeichert werden",
			placeholder: "Branche, was verkauft wird, an wen, was wichtig zu wissen ist.",
			boundaryHint:
				"Eine Grenze, kein Kontext — sie erreicht den Agenten als eigener Block, vor der Persona.",
			logoAlt: "Logo des Geschäfts",
			logoCaption: "Beim Onboarding erfasst. Unter Marke ersetzen.",
			whatLabel: "Was das Unternehmen macht",
			whatHint: "Kontext. Gilt für alle Agenten, ohne es in jeder Persona zu wiederholen.",
			rulesLabel: "Feste Regeln",
			rulesPlaceholder: "Was kein Agent sagen, versprechen oder garantieren darf.",
		},
		company: {
			subtitle: "Was ein Vertrag, ein Angebot oder eine Quittung über den Aussteller druckt",
			legalName: "Firmenname",
			legalNameHint:
				"Der Name, unter dem ein Dokument ausgestellt wird, wenn er vom Namen des Workspace abweicht.",
			taxIdHint:
				"Wird so gespeichert, wie Sie es schreiben. Der Feldname kommt aus dem Land des Workspace.",
			taxId: "Steuernummer",
			taxIdNamed: "Steuernummer ({local})",
			emailInvalid: "Das At-Zeichen fehlt — hier antwortet ein Kunde auf das Dokument.",
			phoneHint:
				"Wie sie gedruckt werden soll. Es ist nicht die Nummer, die Benachrichtigungen empfängt — die steht unter „Ihr Profil“.",
			adminOnly: "Nur ein Administrator ändert die Unternehmensdaten.",
			failed: "konnte nicht gespeichert werden",
			line1: "Adresse",
			line1Placeholder: "Straße, Allee, Hausnummer",
			region: "Region / Bundesland",
			postalCode: "Postleitzahl",
			country: "Land",
			title: "Das Unternehmen",
			line2: "Zweite Zeile",
			line2Placeholder: "Zusatz, Etage, Gebäude",
			city: "Stadt",
			contactEmail: "Kontakt-E-Mail",
			phone: "Telefon",
		},
		presence: {
			mine: "Meine Präsenz",
			state: "Mein Status",
			available: "verfügbar",
			busy: "beschäftigt",
			away: "abwesend",
			atMost: "höchstens",
			noCeiling: "keine Obergrenze",
			ceilingLabel: "Obergrenze gleichzeitiger Konversationen",
			conversations: "{n} Konversation | {n} Konversationen",
			conversationsOf: "{n} Konversation von {max} | {n} Konversationen von {max}",
		},
		signature: {
			lead: "Was der Kunde vor den Worten liest, wenn eine Person antwortet. Nachrichten des Kundenbetreuers und der Automatisierungen werden nicht signiert — wer will, dass der Kundenbetreuer sich vorstellt, schreibt das in seine Persona.",
			none: "Nicht signieren",
			noneHint: "Nur die Worte, so wie immer.",
			name: "Name der antwortenden Person",
			nameHint: "Wer gerade an der Tastatur sitzt.",
			titleOnly: "Nur die Funktion",
			titleOnlyHint: "Das Team spricht mit einer Stimme.",
			nameTitle: "Name und Funktion",
			nameTitleHint: "Beides, getrennt durch einen Gedankenstrich.",
			title: "Signatur unter den Antworten",
			titleLabel: "Funktion",
			titlePlaceholder: "Kundenbetreuer",
			howItReads: "So wird der Kunde es lesen",
			sample: "Hallo! Ich habe Ihre Bestellung schon zurückgelegt, sie kommt am Donnerstag.",
			noSignature: "Ohne Signatur: Der Kunde erfährt nicht, wer geantwortet hat.",
			adminOnly: "Nur ein Administrator ändert das.",
			failed: "konnte nicht gespeichert werden",
		},
		teams: {
			title: "Teams",
			subtitle: "Gruppen, an die eine Konversation übergeben werden kann",
			notAccess:
				"Teams ordnen die Service-Warteschlange und die Benachrichtigungen. Sie ändern nicht, was jede Person sieht — dafür nutzen Sie Zugriffsprofile unter Einstellungen → Zugriffe.",
			notAccessLink: "Zugriffe öffnen",
			empty:
				"Noch kein Team. Eine ohne Team übergebene Konversation bleibt in der allgemeinen Warteschlange.",
			members: "{n} Person | {n} Personen",
			whoIsIn: "wer dabei ist",
			failed: "hat nicht geklappt",
			itIsYou: "das sind Sie",
			soleOwner: "der einzige aktive Inhaber",
			neverSignedIn: "noch nie angemeldet",
			error: {
				lastOwner:
					"Dieser Workspace braucht mindestens einen aktiven Inhaber. Befördern Sie zuerst jemand anderen.",
				forbidden: "Das darf nur ein Administrator.",
				notFound: "Diese Person ist nicht mehr in diesem Workspace.",
				network: "Der Server war nicht erreichbar.",
				failed: "Diese Änderung konnte nicht gespeichert werden.",
			},
			now: "jetzt",
			minutesAgo: "vor {n} Min.",
			hoursAgo: "vor {n} Std.",
			daysAgo: "vor {n} Tag | vor {n} Tagen",
		},
		account: {
			title: "Ihr Profil",
			subtitle: "Ihr Name, Ihre Rolle und Ihr Passwort",
			photoReplace: "Foto ersetzen",
			photoUpload: "Foto hochladen",
			photoFailed: "das Foto konnte nicht hochgeladen werden",
			photoRemoveFailed: "das Foto konnte nicht entfernt werden",
			failed: "konnte nicht gespeichert werden",
			wrongPassword: "das aktuelle Passwort stimmt nicht",
			passwordFailed: "das Passwort konnte nicht geändert werden",
			phoneInvalid:
				"Beginnen Sie mit + und der Ländervorwahl — ohne sie ist die Nummer zwischen Ländern mehrdeutig.",
			phoneHint:
				"Hier erreicht Sie eine Benachrichtigung außerhalb des Panels. Leer lassen, um nichts zu erhalten.",
			emailNote: "Anmelde-E-Mail: {email}",
			passwordNote:
				"Beim Ändern fallen alle Ihre Sitzungen — auch diese. Sie kommen mit dem neuen Passwort zurück.",
			yourName: "Ihr Name",
			yourWhatsapp: "Ihr WhatsApp",
			saved: "Gespeichert.",
			changePassword: "Passwort ändern",
			currentPassword: "Aktuelles Passwort",
			newPassword: "Neues Passwort",
			changeEmail: "Anmelde-E-Mail ändern",
			newEmail: "Neue E-Mail",
			sendLink: "Bestätigungslink senden",
			emailSent:
				"Wir haben einen Link an {email} geschickt. Öffne ihn zur Bestätigung — bis dahin meldest du dich mit der aktuellen E-Mail an.",
			emailTaken: "Diese E-Mail wird bereits von einem anderen Konto verwendet.",
			emailInvalid: "Diese E-Mail scheint nicht gültig zu sein.",
			emailSame: "Das ist bereits deine E-Mail.",
			emailFailed: "die Änderung konnte nicht angefordert werden",
		},
		error: {
			forbidden: "Das darf nur ein Administrator.",
			notFound: "Diese Person ist nicht mehr in diesem Workspace.",
			offline: "Der Server war nicht erreichbar.",
			generic: "Diese Änderung konnte nicht gespeichert werden.",
		},
	},
} as const;
